import type { Authentication, Claims } from '@mntn-dev/authentication-types'
import type { Authorization } from '@mntn-dev/authorization-types'
import { getPrivileges } from '@mntn-dev/authorization-types'
import { MockModel } from '@mntn-dev/domain-mocks'
import {
  OrganizationId,
  OrganizationTypes,
  PartnerProgramKindEnum,
  SessionId,
  TeamId,
  type UserDomainSelectModel,
  UserId,
} from '@mntn-dev/domain-types'
import { getRandomItem } from '@mntn-dev/utilities'

import type { Profile } from './profile.ts'
import type { AuthorizedSession } from './session.ts'

export const MockClaims = MockModel<Claims>(
  ({
    first_name = 'First',
    last_name = 'Last',
    primary_email = 'user@example.com',
  }) => {
    return {
      first_name,
      last_name,
      primary_email,
    } satisfies Claims
  }
)

export const MockAuthentication = <Overrides extends Partial<Authentication>>(
  overrides?: Overrides
) => {
  const claims = MockClaims(overrides?.claims ?? {})

  return {
    claims,
    userId: '',
    sessionId: SessionId(),
  } satisfies Authentication
}

export const MockAuthorization = MockModel<Authorization>(
  ({
    organizationId = OrganizationId(),
    organizationType = getRandomItem(OrganizationTypes),
    teamIds = [TeamId()],
    isActive = true,
    partnerPrograms = [PartnerProgramKindEnum.mntn_credits],
  }) => {
    const privileges = getPrivileges(organizationType)

    return {
      organizationId,
      organizationType,
      teamIds,
      privileges,
      isActive,
      partnerPrograms,
    } satisfies Authorization
  }
)

export const MockProfile = MockModel<Profile>(
  ({
    userId = UserId(),
    emailAddress = `user+${userId}@example.com`,
    firstName = `First${userId}`,
    lastName = `Last${userId}`,
    displayName = `${firstName} ${lastName}`,
    signUpTimestamp = new Date(),
  }) => {
    return {
      userId,
      emailAddress,
      firstName,
      lastName,
      displayName,
      signUpTimestamp,
    } satisfies Profile
  }
)

export const MockSessionFromUser = <
  Overrides extends Partial<UserDomainSelectModel>,
>(
  overrides?: Overrides
) => {
  const profile = MockProfile(overrides ?? {})

  const { emailAddress, firstName, lastName } = profile

  const claims = MockClaims({
    primary_email: emailAddress,
    first_name: firstName,
    last_name: lastName,
  })

  const authn = MockAuthentication({
    userId: '',
    sessionId: SessionId(),
    claims,
    ...(overrides ?? {}),
  })

  const authz = MockAuthorization(overrides ?? {})

  return {
    authn,
    authz,
    profile,
  }
}

export const MockSession = <
  Overrides extends Partial<{
    authn: Partial<Authentication>
    authz: Partial<Authorization>
    profile: Partial<Profile>
  }>,
>(
  overrides?: Overrides
) => {
  const profile = { ...MockProfile({}), ...(overrides?.profile ?? {}) }

  const { emailAddress, firstName, lastName } = profile

  const claims = MockClaims({
    primary_email: emailAddress,
    first_name: firstName,
    last_name: lastName,
    ...overrides?.authn?.claims,
  })

  const authn = MockAuthentication({
    userId: '',
    sessionId: SessionId(),
    claims,
    ...overrides?.profile,
  })

  const authz = { ...MockAuthorization({}), ...(overrides?.authz ?? {}) }

  return {
    authn,
    authz,
    profile,
  } satisfies AuthorizedSession
}

export const MockPrincipal = <
  Overrides extends Partial<{
    authz: Partial<Authorization>
    profile: Partial<Profile>
  }>,
>(
  overrides?: Overrides
) => {
  const authz = MockAuthorization(overrides?.authz ?? {})
  const profile = MockProfile(overrides?.profile ?? {})

  return {
    authz,
    profile,
  }
}
